import { Injectable } from '@angular/core';
import { AbstractBackendService } from '@core/prototypes/backend.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ExceptionHandlerService } from '@core/services/exceptionhandler/exception.handler.service';
import { catchError, from, Observable, tap } from 'rxjs';
import { SkillFilter, SkillListItem, SkillStats } from '@core/models/skill';
import { ListResult } from '@core/prototypes/list.result';
import { httpParamsFrom } from '@core/utils/custom.http.param.encoder';
import { emitSimpleEvent, SIMPLE_EVENTS } from '@core/utils/utils';
import { map } from 'rxjs/operators';

const TWO_HOURS = 1000 * 60 * 60 * 2;

@Injectable({
  providedIn: 'root'
})
export class SkillsService extends AbstractBackendService<SkillListItem> {
  constructor(
    http: HttpClient,
    private exceptionService: ExceptionHandlerService
  ) {
    super(`/api/employees/`, http);
  }

  /**
   * @inheritDoc
   */
  //eslint-disable-next-line
  override list(filter: SkillFilter, emitEvent?: boolean): Observable<ListResult<SkillListItem>> {
    const myFilter = { ...filter, sorting: undefined, sortDirection: Reflect.get(filter, 'sorting') };
    const httpParams = httpParamsFrom(myFilter);

    return filter.type === 'skill'
      ? this.listSkills(myFilter, emitEvent, httpParams)
      : from(this.listCoreCompetences(myFilter, emitEvent, httpParams));
  }

  private listSkills(
    filter: SkillFilter,
    emitEvent: undefined | boolean,
    params: HttpParams
  ): Observable<ListResult<SkillListItem>> {
    return this.http.get<ListResult<SkillListItem>>(this.endPointUrl + 'search/skills/stats', { params }).pipe(
      tap((result) => {
        result.results.forEach((skill) => {
          skill.type = 'skill';
        });
        if (emitEvent) {
          emitSimpleEvent(SIMPLE_EVENTS.DOWNLOAD_PROGRESS, { filter, result });
        }
      }),
      catchError(this.exceptionService.handleError<ListResult<SkillListItem>>())
    );
  }
  private listCoreCompetences(
    filter: SkillFilter,
    emitEvent: undefined | boolean,
    params: HttpParams
  ): Observable<ListResult<SkillListItem>> {
    const coreCompetencesEndpoint = this.endPointUrl + 'search/skills/stats';
    return this.http.get<ListResult<any>>(coreCompetencesEndpoint, { params }).pipe(
      map((marklogicResult) => {
        const transformedResults: SkillListItem[] = marklogicResult.results.map((item: any) => {
          return {
            skill: item.skill,
            count: item.count,
            type: 'coreCompetence',
          } as SkillListItem;
        });

        const listResult: ListResult<SkillListItem> = {
          totalNumberResultsets: marklogicResult.totalNumberResultsets,
          currentStartIndex: marklogicResult.currentStartIndex,
          currentEndIndex: marklogicResult.currentEndIndex,
          results: transformedResults,
          debug: marklogicResult.debug ?? null
        } as ListResult<SkillListItem>;

        if (emitEvent) {
          emitSimpleEvent(SIMPLE_EVENTS.DOWNLOAD_PROGRESS, { filter, result: listResult });
        }
        return listResult;
      }),
      catchError(this.exceptionService.handleError<ListResult<SkillListItem>>())
    );
  };

  autocompleteSkills(text: string, type: string): Observable<string[]> {
    let params = new HttpParams();
    params = params.set('searchValue', text);
    params = params.set('type', type);
    return this.http.get<ListResult<SkillListItem>>(this.endPointUrl + 'search/skills/stats', { params }).pipe(
      map((marklogicResult: ListResult<SkillListItem>) => {
        if (marklogicResult && marklogicResult.results && Array.isArray(marklogicResult.results)) {
          return marklogicResult.results.map(item => item.skill);
        }
        return [];
      }),
      catchError(this.exceptionService.handleError<string[]>())
    );
  }

  public getSkillCounts(skill: string, type: string): Observable<SkillStats> {
    return this.http
      .get<SkillStats>(this.endPointUrl + `search/skills/${encodeURIComponent(skill)}/count?type=${encodeURIComponent(type)}`)
      .pipe(catchError(this.exceptionService.handleError<SkillStats>()));
  }
}
